import { useState } from "react";
import { descriviVerdetto } from "../../i18n/rilascio.ts";
import { NOMI_OPERAZIONE, type useEsecuzione } from "./DeployConsole.tsx";
import { Button, ErrorNotice, Input, Menu, VoceMenu } from "./ui.tsx";
import { api } from "../lib/api.ts";
import { useT } from "../lib/i18n.tsx";
import { useAction } from "../lib/hooks.ts";
import { linkProps, navigate } from "../lib/router.ts";
import type { Scheda, SezioneRilascio } from "../lib/risorse.ts";
import { azionePrincipale, dataSqlite, statoStack } from "../lib/stato-stack.ts";
import type { Session, Stack, StackConRiepilogo, VerdettoRilascio } from "../lib/types.ts";
import { BadgeStato, ESITO_RILASCIO, testoDominio } from "../pages/Stacks.tsx";

type Esecuzione = ReturnType<typeof useEsecuzione>;

/**
 * La testata del dettaglio: nome e stato, l'azione che serve adesso, le altre
 * nel menu ⋯, e sotto una striscia che risponde a «come sta?» senza aprire
 * schede. Ogni riquadro porta alla scheda dove se ne sa di più.
 */
export function Intestazione({
  stack,
  verdetto,
  sessione,
  esecuzione,
  onCambiato,
  onScheda,
}: {
  stack: StackConRiepilogo;
  /** `null` finché non arriva (o se non arriva): la striscia ripiega sulla strategia configurata. */
  verdetto: VerdettoRilascio | null;
  sessione: Session;
  esecuzione: Esecuzione;
  onCambiato: () => void;
  onScheda: (s: Scheda, sezione?: SezioneRilascio) => void;
}) {
  const t = useT();
  const puòAvviare = sessione.permissions.includes("stack:deploy");
  const puòScrivere = sessione.permissions.includes("stack:write");
  const inCorso = esecuzione.esito === "in-corso";
  const avviato = stack.deployedRevisionId !== null;
  const azione = azionePrincipale(stack, sessione.permissions);
  const avvia = (percorso: string) => void esecuzione.avvia(`/stacks/${stack.id}/${percorso}`);

  const duplicazione = useAction(async (name: string) => {
    const { stack: copia } = await api.post<{ stack: Stack }>(`/stacks/${stack.id}/duplica`, { name });
    // La prima cosa da fare su una copia è cambiarne le variabili: la si apre subito.
    navigate(`/stacks/${copia.id}`);
  });
  const eliminazione = useAction(async () => {
    await api.delete(`/stacks/${stack.id}`);
    navigate("/stacks");
  });

  const voci = [
    puòScrivere && puòAvviare && (
      <VoceMenu
        key="rinomina"
        disabled={inCorso}
        onClick={() => {
          // ponytail: prompt nativo, un dialogo vero quando servirà più del nome
          const name = window
            .prompt(t.t("dettaglio.rinominaPrompt", { nome: stack.name, avviato }), stack.name)
            ?.trim();
          // Il server ferma lo stack, copia i volumi e lo riavvia: i passaggi si leggono nella console.
          if (name && name !== stack.name) avvia(`rinomina/${encodeURIComponent(name)}`);
        }}
      >
        {t.t("dettaglio.rinomina")}
      </VoceMenu>
    ),
    puòScrivere && (
      <VoceMenu
        key="duplica"
        disabled={inCorso || duplicazione.running}
        onClick={() => {
          // I segreti li copia il server: quelli che vede il browser sono mascherati.
          const name = window
            .prompt(t.t("dettaglio.duplicaPrompt", { nome: stack.name }), t.t("dettaglio.duplicaNome", { nome: stack.name }))
            ?.trim();
          if (name) void duplicazione.run(name);
        }}
      >
        {t.t("dettaglio.duplica")}
      </VoceMenu>
    ),
    puòAvviare && avviato && (
      <VoceMenu key="riavvia" disabled={inCorso} onClick={() => avvia("restart")}>
        {t.t("dettaglio.riavvia")}
      </VoceMenu>
    ),
    puòAvviare && avviato && (
      <VoceMenu key="ferma" disabled={inCorso} onClick={() => avvia("stop")}>
        {t.t("dettaglio.ferma")}
      </VoceMenu>
    ),
    // Solo per uno stack fermo: il server rifiuta comunque, la voce lo dice prima.
    puòScrivere && (
      <VoceMenu
        key="elimina"
        pericolosa
        disabled={avviato || inCorso || eliminazione.running}
        onClick={() => {
          if (window.confirm(t.t("dettaglio.eliminaConferma", { nome: stack.name }))) void eliminazione.run();
        }}
      >
        {t.t("comune.elimina")}
        {avviato && <span className="block text-xs tenue">{t.t("dettaglio.fermaPrima")}</span>}
      </VoceMenu>
    ),
  ].filter(Boolean);

  return (
    <div className="space-y-4">
      <div>
        <a {...linkProps("/stacks")} className="text-sm tenue hover:underline">
          ← {t.t("nav.stack")}
        </a>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="min-w-0 break-words text-xl font-semibold">{stack.name}</h1>
          {/* Quale generazione stia servendo il traffico era visibile solo con `docker ps`. */}
          <span title={stack.activeSlot ? t.t("dettaglio.generazioneAttiva", { slot: stack.activeSlot }) : undefined}>
            <BadgeStato stato={statoStack(stack)} {...(stack.activeSlot ? { dettaglio: stack.activeSlot } : {})} />
          </span>

          <div className="ml-auto flex items-center gap-2">
            {voci.length > 0 && (
              <Menu
                etichetta={
                  <>
                    <span aria-hidden>⋯</span>
                    <span className="sr-only">{t.t("dettaglio.altreAzioni")}</span>
                  </>
                }
              >
                {voci}
              </Menu>
            )}
            {azione && (
              <Button variant="primary" loading={inCorso} onClick={() => avvia("deploy")}>
                {azione.tipo === "avvia"
                  ? t.t("dettaglio.avvia")
                  : t.t(azione.tipo === "rilascia" ? "dettaglio.rilascia" : "dettaglio.riprova", { n: azione.revisione })}
              </Button>
            )}
          </div>
        </div>

        {puòScrivere ? (
          <DescrizioneStack stack={stack} onSalvata={onCambiato} />
        ) : (
          stack.description && <p className="mt-0.5 text-sm tenue">{stack.description}</p>
        )}
        {inCorso && <AvvisoInCorso esecuzione={esecuzione} />}
        <ErrorNotice error={duplicazione.error ?? eliminazione.error} />
      </div>

      <Striscia stack={stack} verdetto={verdetto} onScheda={onScheda} />
    </div>
  );
}

/** Quattro riquadri, ognuno un pulsante verso la scheda che ne dice di più. */
function Striscia({
  stack,
  verdetto,
  onScheda,
}: {
  stack: StackConRiepilogo;
  verdetto: VerdettoRilascio | null;
  onScheda: (s: Scheda, sezione?: SezioneRilascio) => void;
}) {
  const t = useT();
  const { domini, container, revisioneAttiva, ultimoRilascio } = stack.riepilogo;
  const blueGreen = stack.deployStrategy === "blue-green";
  // Blue/green configurato ma senza i requisiti: il prossimo avvio ricrea lo
  // stesso, e la striscia lo dice con la frase del verdetto.
  const nota = blueGreen && verdetto && !verdetto.pronto ? descriviVerdetto(verdetto, t).effetto : undefined;
  const riquadri: {
    titolo: string;
    valore: string;
    nota?: string | undefined;
    scheda: Scheda;
    sezione?: SezioneRilascio;
  }[] = [
    {
      titolo: t.t("stacks.colStrategia"),
      valore: t.t(blueGreen ? "striscia.blueGreen" : "striscia.ricreazione"),
      nota,
      scheda: "rilascio",
      sezione: "strategia",
    },
    { titolo: t.t("stacks.colDominio"), valore: testoDominio(domini), scheda: "rilascio", sezione: "instradamenti" },
    {
      titolo: t.t("striscia.inEsecuzione"),
      valore:
        stack.deployedRevisionId === null
          ? t.t("striscia.fermo")
          : [
              revisioneAttiva !== null && t.t("stacks.revisione", { n: revisioneAttiva }),
              container ? t.t("striscia.sani", container) : "—",
            ]
              .filter(Boolean)
              .join(" · "),
      scheda: "panoramica",
    },
    {
      titolo: t.t("stacks.colRilascio"),
      valore: ultimoRilascio
        ? `${t.t(ESITO_RILASCIO[ultimoRilascio.esito])} ${t.tempoFa(dataSqlite(ultimoRilascio.quando))}`
        : "—",
      scheda: "storico",
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      {riquadri.map((r) => (
        <button
          key={r.titolo}
          onClick={() => onScheda(r.scheda, r.sezione)}
          className="superficie min-w-0 rounded-xl px-3.5 py-2.5 text-left hover:bg-[var(--superficie-tenue)]"
        >
          <span className="block text-xs tenue">{r.titolo}</span>
          {/* Due righe, non una: a 390px «~1 s di interruzione» verrebbe tagliato. */}
          <span className="line-clamp-2 break-words text-sm font-medium" title={r.valore}>
            {r.valore}
          </span>
          {r.nota && <span className="mt-0.5 block text-xs text-amber-700 dark:text-amber-400">{r.nota}</span>}
        </button>
      ))}
    </div>
  );
}

/** «di marco», non «avviato da»: il nome dell'operazione è a volte maschile, a volte femminile. */
function AvvisoInCorso({ esecuzione }: { esecuzione: Esecuzione }) {
  const t = useT();
  return (
    <p className="mt-1 text-xs tenue">
      {t.t("dettaglio.operazioneInCorso", {
        operazione: t.t(esecuzione.azione ? NOMI_OPERAZIONE[esecuzione.azione] : "console.operazione"),
        attore: esecuzione.attore,
      })}
    </p>
  );
}

/** La descrizione si modifica dove la si legge: un clic, Invio per salvare, Esc per lasciar stare. */
function DescrizioneStack({ stack, onSalvata }: { stack: Stack; onSalvata: () => void }) {
  const t = useT();
  const [bozza, setBozza] = useState<string | null>(null);
  const salvataggio = useAction(async (description: string) => {
    await api.patch(`/stacks/${stack.id}`, { description });
    setBozza(null);
    onSalvata();
  });

  if (bozza === null) {
    return (
      <button
        onClick={() => setBozza(stack.description ?? "")}
        title={t.t("dettaglio.modificaDescrizione")}
        className="mt-0.5 block text-left text-sm tenue hover:underline hover:decoration-dotted"
      >
        {stack.description ?? t.t("dettaglio.aggiungiDescrizione")}
      </button>
    );
  }

  return (
    <div className="mt-1 w-[32rem] max-w-full space-y-1">
      <div className="flex items-center gap-2">
        <Input
          value={bozza}
          onChange={(e) => setBozza(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void salvataggio.run(bozza);
            if (e.key === "Escape") setBozza(null);
          }}
          maxLength={500}
          placeholder={t.t("dettaglio.descrizioneSegnaposto")}
          aria-label={t.t("dettaglio.descrizione")}
          className="min-w-0 flex-1 text-sm"
          autoFocus
        />
        <Button variant="primary" className="text-xs" loading={salvataggio.running} onClick={() => void salvataggio.run(bozza)}>
          {t.t("comune.salva")}
        </Button>
        <Button variant="ghost" className="text-xs" onClick={() => setBozza(null)}>
          {t.t("comune.annulla")}
        </Button>
      </div>
      <ErrorNotice error={salvataggio.error} />
    </div>
  );
}
